import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { dealsApi } from "@/api/endpoints";
import type { DealDetail, DealDocument, DocumentSection } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Note } from "@/components/ui/Note";
import { ActionHint, Panel, PanelActions, PanelHead, Spacer } from "@/components/ui/Panel";
import { useI18n } from "@/i18n/use-i18n";
import { cn } from "@/lib/cn";

interface StepProps {
  deal: DealDetail;
  /** Someone else's Vorgang: show it, but offer nothing that writes. */
  readOnly: boolean;
  onChanged: (deal: DealDetail) => void;
  onNext: () => void;
  onBack: () => void;
}

export function ContractStep({ deal, readOnly, onChanged, onNext, onBack }: StepProps) {
  const { t } = useI18n();
  // Folded into the existing lock: it both suppresses the auto-generate
  // effect below and swaps the release button for plain navigation.
  const locked = readOnly || deal.signature_request !== null;
  // One open document at a time: the annexes are long, and a page of several
  // expanded contracts is unreadable.
  const [openId, setOpenId] = useState<string | null>(null);

  const generate = useMutation({
    mutationFn: () => dealsApi.generateContract(deal.id),
    onSuccess: () => void refetchDeal(),
  });

  const release = useMutation({
    mutationFn: () => dealsApi.releaseContract(deal.id),
    onSuccess: onChanged,
  });

  async function refetchDeal() {
    onChanged(await dealsApi.detail(deal.id));
  }

  // The package is generated on arrival so the salesperson sees documents
  // immediately rather than having to press a button whose only job is setup.
  useEffect(() => {
    if (deal.documents.length === 0 && !locked && !generate.isPending && !generate.isError) {
      generate.mutate();
    }
    // Intentionally keyed on the emptiness of the list, not on `generate`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.documents.length, locked]);

  return (
    <Panel>
      <PanelHead eyebrow={t("contract.eyebrow")} title={t("contract.title")}>
        {t("contract.intro")}
      </PanelHead>

      {generate.isPending && <p className="text-muted">{t("contract.generating")}</p>}

      {generate.isError && (
        <Note tone="error" className="mb-4">
          {(generate.error as Error).message}
        </Note>
      )}

      {deal.documents.some((d) => d.has_details) && (
        <p className="mb-2.5 text-[12.5px] text-muted">{t("contract.expandHint")}</p>
      )}

      <div className="space-y-2">
        {deal.documents.map((document) => (
          <DocumentRow
            key={document.id}
            dealId={deal.id}
            document={document}
            open={openId === document.id}
            onToggle={() => setOpenId(openId === document.id ? null : document.id)}
          />
        ))}
      </div>

      {deal.missing_signature_documents.length > 0 && (
        <Note tone="warn" className="mt-4.5">
          <b>{t("contract.missingTitle")}</b>{" "}
          {t("contract.missingBody", { names: deal.missing_signature_documents.join(", ") })}
        </Note>
      )}

      {deal.documents.length > 0 && (
        <Note tone="info" className="mt-4.5">
          <b>{t("contract.checkNowTitle")}</b> {t("contract.checkNowBody")}
        </Note>
      )}

      {release.isError && (
        <Note tone="error" className="mt-4">
          {(release.error as Error).message}
        </Note>
      )}

      <PanelActions>
        <Button variant="ghost" onClick={onBack}>
          {t("common.back")}
        </Button>
        <Spacer />
        <ActionHint>{t("contract.hint")}</ActionHint>
        {locked ? (
          <Button onClick={onNext}>{t("contract.continue")}</Button>
        ) : (
          <Button
            busy={release.isPending}
            disabled={deal.documents.length === 0}
            onClick={() => release.mutate(undefined, { onSuccess: onNext })}
          >
            {t("contract.release")}
          </Button>
        )}
      </PanelActions>
    </Panel>
  );
}

interface RowProps {
  dealId: string;
  document: DealDocument;
  open: boolean;
  onToggle: () => void;
}

/** One document in the package; expands to its own merged text. */
function DocumentRow({ dealId, document, open, onToggle }: RowProps) {
  const { t } = useI18n();
  const panelId = `document-${document.id}`;

  // Fetched on expand rather than with the deal, so opening a Vorgang does not
  // drag the full text of every annex along with it. Cached afterwards, so
  // collapsing and reopening costs nothing.
  const detail = useQuery({
    queryKey: ["contract-document", document.id],
    queryFn: () => dealsApi.contractDocument(dealId, document.id),
    enabled: open && document.has_details,
    staleTime: Infinity,
  });

  const signable = document.requires_signature;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-[9px] border bg-surface",
        signable ? "border-teal/45 bg-teal-wash/35" : "border-line",
      )}
    >
      <RowHeader
        document={document}
        open={open}
        onToggle={onToggle}
        panelId={panelId}
        signableLabel={t(signable ? "contract.signable" : "contract.attachment")}
        noFileLabel={t("contract.noTemplate")}
      />

      {open && document.has_details && (
        <div
          id={panelId}
          className="border-t border-line bg-page px-3.5 py-4 text-[13.5px] leading-relaxed sm:px-4.5"
        >
          {/* Deliberately not translated: this is the German legal text the
              customer signs, and an English paraphrase would misrepresent it.
              In English the caption below says so; in German it is empty. */}
          {t("contract.previewLanguageNote") && (
            <p className="mb-3 text-[12.5px] text-muted">{t("contract.previewLanguageNote")}</p>
          )}
          {detail.isPending && <p className="text-muted">{t("contract.loadingDetails")}</p>}
          {detail.isError && (
            <Note tone="error">{(detail.error as Error).message}</Note>
          )}
          {detail.data?.sections.map((section, index) => (
            <Section key={index} section={section} />
          ))}
        </div>
      )}
    </div>
  );
}

function RowHeader({
  document,
  open,
  onToggle,
  panelId,
  signableLabel,
  noFileLabel,
}: {
  document: DealDocument;
  open: boolean;
  onToggle: () => void;
  panelId: string;
  signableLabel: string;
  noFileLabel: string;
}) {
  const inner = (
    <>
      <span
        aria-hidden="true"
        className="grid size-[18px] shrink-0 place-items-center rounded bg-ok text-[11px] font-bold text-white"
      >
        ✓
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-semibold">{document.name}</span>
        <span className="block text-[12.5px] text-muted">
          {document.description}
          {!document.has_details && ` · ${noFileLabel}`}
        </span>
      </span>
      <span
        className={cn(
          "shrink-0 text-[11.5px] font-semibold whitespace-nowrap",
          document.requires_signature ? "text-teal-deep" : "text-muted",
        )}
      >
        {signableLabel}
      </span>
    </>
  );

  // Only rows with a template behind them are interactive: a button that opens
  // nothing is worse than no button.
  if (!document.has_details) {
    return <div className="flex items-center gap-3 px-3.5 py-3">{inner}</div>;
  }

  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={panelId}
      onClick={onToggle}
      className="flex w-full items-center gap-3 px-3.5 py-3 text-left hover:bg-teal-wash/40"
    >
      {inner}
      <span
        aria-hidden="true"
        className={cn(
          "shrink-0 text-[11px] text-muted transition-transform",
          open && "rotate-180",
        )}
      >
        ▼
      </span>
    </button>
  );
}

function Section({ section }: { section: DocumentSection }) {
  return (
    <section className="mb-3.5 last:mb-0">
      {section.heading && (
        <h5
          className={cn(
            "mb-1.5 font-bold",
            section.level <= 1
              ? "text-[13px] tracking-[0.08em] text-muted uppercase"
              : "text-[13.5px] text-navy-deep",
          )}
        >
          {section.heading}
        </h5>
      )}
      {section.blocks.map((block, index) =>
        block.kind === "li" ? (
          <p key={index} className="mb-1 flex gap-2 pl-1">
            <span aria-hidden="true" className="text-muted">
              ·
            </span>
            <span>
              <Runs runs={block.runs} />
            </span>
          </p>
        ) : (
          <p key={index} className="mb-1.5">
            <Runs runs={block.runs} />
          </p>
        ),
      )}
    </section>
  );
}

/** Renders a paragraph, highlighting the values merged in from the deal. */
function Runs({ runs }: { runs: DocumentSection["blocks"][number]["runs"] }) {
  return (
    <>
      {runs.map((run, index) =>
        run.merged ? (
          <mark
            key={index}
            className="rounded bg-teal-wash px-1 py-0.5 font-semibold break-words text-navy-deep"
          >
            {run.text}
          </mark>
        ) : (
          <span key={index}>{run.text}</span>
        ),
      )}
    </>
  );
}
